import axios, { AxiosInstance } from 'axios';
import { StorageLocation } from '../types';

export class StorageService {
  private client: AxiosInstance;

  constructor(baseUrl: string = 'http://localhost:8080/api') {
    this.client = axios.create({
      baseURL: baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  // Get all storage locations
  async getStorageLocations(namespace?: string): Promise<StorageLocation[]> {
    try {
      const url = namespace
        ? `/v1/namespaces/${namespace}/backup-storages`
        : `/v1/backup-storages`;
      const response = await this.client.get(url);
      return response.data;
    } catch (_error) {
      console.warn(
        'Falling back to mock data: API call to get storage locations failed',
        _error,
      );
      return this.getMockStorageLocations();
    }
  }

  // Get single storage location
  async getStorageLocation(
    namespace: string,
    name: string,
  ): Promise<StorageLocation> {
    try {
      const response = await this.client.get(
        `/v1/namespaces/${namespace}/backup-storages/${name}`,
      );
      return response.data;
    } catch (_error) {
      console.warn(
        `Falling back to mock data: API call to get storage location '${namespace}/${name}' failed`,
        _error,
      );
      const mockStorage = this.getMockStorageLocations().find(
        s => s.metadata.name === name,
      );
      if (!mockStorage) {
        throw new Error(`Storage location '${name}' not found`);
      }
      return mockStorage;
    }
  }

  // Create storage location
  async createStorageLocation(
    storage: StorageLocation,
  ): Promise<StorageLocation> {
    try {
      const response = await this.client.post(
        `/v1/namespaces/${storage.metadata.namespace}/backup-storages`,
        storage,
      );
      return response.data;
    } catch (_error) {
      console.warn(
        'Falling back to mock data: API call to create storage location failed',
        _error,
      );
      return storage;
    }
  }

  // Update storage location
  async updateStorageLocation(
    namespace: string,
    name: string,
    storage: Partial<StorageLocation>,
  ): Promise<StorageLocation> {
    try {
      const response = await this.client.put(
        `/v1/namespaces/${namespace}/backup-storages/${name}`,
        storage,
      );
      return response.data;
    } catch (_error) {
      console.warn(
        `Falling back to mock data: API call to update storage location '${namespace}/${name}' failed`,
        _error,
      );
      return this.getMockStorageLocations()[0];
    }
  }

  // Delete storage location
  async deleteStorageLocation(namespace: string, name: string): Promise<void> {
    try {
      await this.client.delete(
        `/v1/namespaces/${namespace}/backup-storages/${name}`,
      );
    } catch (_error) {
      console.log(`Mock: Deleted storage ${namespace}/${name}`);
    }
  }

  // Test storage location connection
  async testStorageConnection(storage: StorageLocation): Promise<boolean> {
    try {
      const response = await this.client.post(
        `/v1/backup-storages/test`,
        storage,
      );
      return response.data.success;
    } catch (_error) {
      console.warn(
        'Falling back to mock data: API call to test storage connection failed',
        _error,
      );
      return true; // Mock: connection successful
    }
  }

  // Mock data helpers
  private getMockStorageLocations(): StorageLocation[] {
    return [
      {
        metadata: {
          name: 's3-backup',
          namespace: 'everest-system',
        },
        spec: {
          type: 's3',
          bucketName: 'my-database-backups',
          region: 'us-east-1',
          verifyTLS: true,
          credentials: {
            accessKeyId: 'AKIAIOSFODNN7EXAMPLE',
            secretAccessKey: '***',
          },
        },
        status: {
          usedBy: ['pg-production', 'mongo-analytics'],
        },
      },
      {
        metadata: {
          name: 'azure-backup',
          namespace: 'everest-system',
        },
        spec: {
          type: 'azure',
          bucketName: 'everest-backups',
          credentials: {
            accountName: 'mystorageaccount',
            accountKey: '***',
          },
        },
        status: {
          usedBy: [],
        },
      },
      {
        metadata: {
          name: 'gcs-backup',
          namespace: 'everest-system',
        },
        spec: {
          type: 'gcs',
          bucketName: 'everest-gcs-backups',
          credentials: {
            credentialsFilePath: '/etc/gcs/credentials.json',
          },
        },
        status: {
          usedBy: [],
        },
      },
    ];
  }
}

export const storageService = new StorageService();
